import React, { useCallback, useState } from 'react'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Formik } from 'formik'
import * as Yup from 'yup'
import AddWallboxFormFields from 'src/Libs/wallboxes/components/dialogs/AddWallboxFormFields'
import { AddWallboxFormModel } from 'src/Libs/wallboxes/models'
import { addFormToDto, useWallboxes } from 'src/Libs/wallboxes'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { ApiResult } from 'src/Libs/api'

export const ipv4 = Yup.string()
  .matches(/(^(\d{1,3}\.){3}(\d{1,3})$)/, {
    message: 'Invalid IP address',
    excludeEmptyString: true,
  })
  .test('ip', 'Invalid IP address', value => {
    return value === undefined || value.trim() === '' ? true : value.split('.').find(i => parseInt(i, 10) > 255) === undefined
  })

export interface AddWallboxDialogProps {
  place: number
  onClose: () => void
  storageSerialNr: string
}

const AddWallboxDialog: React.VFC<AddWallboxDialogProps> = ({ place, onClose, storageSerialNr }) => {
  const { t } = useCustomTranslation()
  const { addWallbox } = useWallboxes()
  const { enqueueErrorStack, enqueueSuccessStack } = useSnackbar()

  const [formData] = useState<AddWallboxFormModel>({ ipAddress: '' })
  const handleSubmit = useCallback(
    (formData: AddWallboxFormModel) => {
      const dto = addFormToDto(formData)
      addWallbox(storageSerialNr, dto)
        .then((result: ApiResult<number>) => {
          if (result.hasErrors) {
            enqueueErrorStack(t('systemSettings.wallbox.addNew.error.common'))
          } else {
            enqueueSuccessStack(t('systemSettings.wallbox.addNew.success.common'))
            onClose()
          }
        })
        .catch(error => {
          console.error(error)
          enqueueErrorStack(t('systemSettings.wallbox.addNew.error.common'))
        })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onClose]
  )

  const formSchema = Yup.object({
    ipAddress: Yup.string()
      .matches(/(^(\d{1,3}\.){3}(\d{1,3})$)/, {
        message: t('validators.ipAddress.mismatch'),
        excludeEmptyString: true,
      })
      .test('ip', t('validators.ipAddress.mismatch'), value => {
        return value === undefined || value.trim() === '' ? true : value.split('.').find(i => parseInt(i, 10) > 255) === undefined
      }),
  })

  return (
    <Formik initialValues={formData} validationSchema={formSchema} onSubmit={handleSubmit}>
      {({ handleSubmit, dirty, touched, errors, isValid }) => {
        return (
          <Dialog
            title={t('systemSettings.wallbox.addNew.title')}
            onClose={() => onClose()}
            place={place}
            maxWidth="sm"
            actions={{
              primary: {
                name: t('common.save'),
                disabled: !isValid || !dirty,
                onClick: handleSubmit,
              },
              secondary: [
                {
                  name: t('common.cancel'),
                },
              ],
            }}
          >
            <AddWallboxFormFields />
          </Dialog>
        )
      }}
    </Formik>
  )
}

export default AddWallboxDialog
